import {SlidingTabIndicatorDirective} from "src/app/modules/shared/directives/sliding-tab-indicator.directive";
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  ChangeDetectorRef,
  SimpleChanges,
} from "@angular/core";
import {Subscription} from "rxjs";
import {Notification} from "src/app/interfaces/notification.interface";
import {User, UserDto} from "src/app/modules/auth/models/User";
import {ModalService} from "src/app/modules/core/services/modal.service";
import {ToastService} from "src/app/modules/core/services/toast.service";
import {CustomError} from "src/app/modules/shared/interfaces/CustomError.interface";
import {UserService} from "src/app/modules/shared/services/user.service";
import {NotificationsService} from "src/app/services/notifications.service";
import {PushNotificationsService} from "src/app/services/pushNotifications.service";
import {PushPermissionResponse} from "src/app/interfaces/pushPermission.interface";
import {NgClass} from "@angular/common";
import {NotificationComponent} from "../notification/notification.component";
import {ModalComponent} from "../../modules/core/components/modal/modal.component";
import {TranslateModule} from "@ngx-translate/core";

@Component({
  selector: "app-notifications-list",
  templateUrl: "./notifications-list.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SlidingTabIndicatorDirective,
    NgClass,
    NotificationComponent,
    ModalComponent,
    TranslateModule,
  ],
})
export class NotificationsListComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() user!: User;
  public notifications?: Notification[];
  @Input() isMobile = false;
  private _isOpened = false;
  @Input()
  set isOpened(value: boolean) {
    if (this._isOpened && !value) {
      this.markNotificationsAsRead();
    }
    this._isOpened = value;
  }
  get isOpened(): boolean {
    return this._isOpened;
  }
  public isPushEnabled = false;
  public notificationsType = "unread";
  public notificationsToDisplay?: Notification[];
  private readonly subscriptions: Subscription[] = [];
  private isUserInitialized = false;

  constructor(
    private readonly notificationsService: NotificationsService,
    private readonly cdr: ChangeDetectorRef,
    private readonly toastService: ToastService,
    private readonly modalService: ModalService,
    private readonly pushNotificationsService: PushNotificationsService,
    private readonly userService: UserService
  ) {}

  ngOnInit() {
    const notificationsSubscription = this.notificationsService
      .getNotifications()
      .subscribe((notifications) => {
        this.notifications = notifications;
        this.switchNotifications(this.notificationsType);
        this.cdr.markForCheck();
      });
    const fcmTokenChangedSubscription =
      this.pushNotificationsService.fcmTokenChanged$.subscribe({
        next: ({userId, enabled}) => {
          if (!this.user || userId !== this.user._id) {
            return;
          }

          this.isPushEnabled = enabled;
          this.cdr.markForCheck();
        },
      });

    const modalSubscription = this.modalService.modalOpened.subscribe(
      (event) => {
        // Check if notifications modal is closed (fix for back popstate closing not triggering markNotificationsAsRead)
        if (
          this.isMobile &&
          event?.modalId === "notifications" &&
          !event.opened
        ) {
          this.markNotificationsAsRead();
        }
      }
    );
    this.subscriptions.push(
      notificationsSubscription,
      fcmTokenChangedSubscription,
      modalSubscription
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    this.switchNotifications(this.notificationsType);

    if (changes["user"]?.currentValue && !this.isUserInitialized) {
      this.isUserInitialized = true;
      const checkUserFcmTokensSubscription = this.pushNotificationsService
        .hasStoredFcmTokens(this.user._id)
        .subscribe({
          next: (hasToken) => {
            if (hasToken) {
              this.isPushEnabled = true;
            } else {
              this.isPushEnabled = false;
            }
            this.cdr.markForCheck();
          },
          error: () => {
            this.isPushEnabled = false;
          },
        });

      this.subscriptions.push(checkUserFcmTokensSubscription);
    }
  }

  openModal(modalId: string): void {
    this.modalService.openModal(modalId);
  }

  closeModal(modalId: string): void {
    this.modalService.closeModal(modalId);
  }

  private markNotificationsAsRead() {
    if (!this.user || !this.notifications?.length) return;
    const hasUnread = this.notifications.some(
      (n) => !n.read && !this.notificationsService.requiresAction(n)
    );
    if (!hasUnread) return;

    const readNotificationsSubscription = this.notificationsService
      .markNotificationsAsRead(this.user._id)
      .subscribe({
        error: (error: CustomError) => {
          this.toastService.sendToast({
            text: error.error.error,
            classes: "bg-form-error",
          });
        },
      });

    this.subscriptions.push(readNotificationsSubscription);
  }

  enablePushNotifications() {
    const updatePushSubscription = this.pushNotificationsService
      .requestPermission(this.user._id)
      .subscribe({
        next: (response: PushPermissionResponse) => {
          const isEnabled = response.success || response.status === 409;
          this.isPushEnabled = isEnabled;

          if (response.success) {
            this.toastService.sendToast({
              text: "PUSH_NOTIFICATIONS_ENABLED",
              classes: "bg-form-success",
            });
          } else if (response.status === 409) {
            this.toastService.sendToast({
              text: "PUSH_NOTIFICATIONS_ALREADY_SUBSCRIBED",
              classes: "bg-form-success",
            });
          } else if (response.reason === "blocked") {
            this.toastService.sendToast({
              text: "PUSH_NOTIFICATIONS_BLOCKED",
              classes: "bg-form-error",
            });
          } else {
            this.toastService.sendToast({
              text: "PUSH_NOTIFICATIONS_FAILED",
              classes: "bg-form-error",
            });
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.isPushEnabled = false;
          this.toastService.sendToast({
            text: "PUSH_NOTIFICATIONS_FAILED",
            classes: "bg-form-error",
          });
          this.cdr.markForCheck();
        },
      });
    this.subscriptions.push(updatePushSubscription);
  }

  dismissPushPrompt() {
    this.user.hasDismissedPushPrompt = true;

    const userInformations: UserDto = {
      hasDismissedPushPrompt: true,
    };

    const updateUserSubscription = this.userService
      .updateUser(this.user._id, userInformations)
      .subscribe({
        next: () => {
          // Do nothing
        },
        error: (error: CustomError) => {
          this.toastService.sendToast({
            text: error.error.error,
            classes: "bg-form-error",
          });
        },
      });
    this.subscriptions.push(updateUserSubscription);

    this.cdr.markForCheck();
  }

  deleteNotifications() {
    this.notificationsService.deleteUserNotifications(this.user._id).subscribe({
      next: () => {
        this.closeModal("dlt_notifications");
        this.notifications = [];
        this.notificationsToDisplay = [];
        this.notificationsService.setNotifications([]);
        this.toastService.sendToast({
          text: "NOTIFICATIONS_DELETED",
          classes: "bg-form-success",
        });

        this.cdr.markForCheck();
      },
      error: (error: CustomError) => {
        this.toastService.sendToast({
          text: error.error.error,
          classes: "bg-form-error",
        });
      },
    });
  }

  public switchNotifications(type: string): void {
    this.notificationsType = type;
    this.notificationsToDisplay =
      type !== "all"
        ? this.notifications?.filter((n) =>
            this.notificationsService.isEffectivelyUnread(n)
          ) ?? []
        : this.notifications ?? [];
  }

  trackByFn(_index: number, item: Notification) {
    return item._id;
  }

  onKeyUp(
    event: KeyboardEvent,
    type: string,
    modalId?: string | null,
    notificationsType?: string | null
  ): void {
    if (event.key === "Enter" || event.key === " ") {
      if (type === "openModal" && modalId) {
        this.openModal(modalId);
      } else if (type === "closeModal" && modalId) {
        this.closeModal(modalId);
      } else if (type === "deleteNotifications") {
        this.deleteNotifications();
      } else if (type === "switchNotifications" && notificationsType) {
        this.switchNotifications(notificationsType);
      }
    }
  }

  ngOnDestroy() {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }
}
